import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import RootFractionInput, { formatLatexPreview } from "../../../../components/inputs/RootFractionInput";
import TriangleDiagram from "../../../../components/diagrams/TriangleDiagram";
import { generateAngleLabel } from "./rightTriangleData";

const FUNCTIONS = ["sin", "cos", "tan"];
export const type = "IdentifyTrigRatioFromTriangleTask";

const shuffle = (items) => {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const other = Math.floor(Math.random() * (index + 1));
    [result[index], result[other]] = [result[other], result[index]];
  }
  return result;
};

const functionLatex = (kind, angleLabel = "\\alpha") => `\\${kind}${angleLabel}`;

const normalize = (value) => String(value ?? "")
  .toLowerCase()
  .replace(/\\d?frac\{([^{}]+)\}\{([^{}]+)\}/g, "$1/$2")
  .replace(/[{}()\\\s*]/g, "");

export function TrigRatioDiagram({ labels, mirrored, angleAt, angleLabel = "\\alpha" }) {
  return (
    <TriangleDiagram
      a={4}
      b={3}
      c={5}
      angleAt={angleAt === "bottom" ? "B" : "C"}
      labelA={labels.horizontal}
      labelB={labels.vertical}
      labelC={labels.hypotenuse}
      mirrored={mirrored}
      alphaLabel={angleLabel}
    />
  );
}

export function generateTask(options = {}) {
  const kind = FUNCTIONS.includes(options.kind) ? options.kind : FUNCTIONS[Math.floor(Math.random() * FUNCTIONS.length)];
  const [horizontal, vertical, hypotenuse] = shuffle(["x", "y", "z"]);
  const angleAt = Math.random() < 0.5 ? "bottom" : "top";
  const mirrored = Math.random() < 0.5;
  const angleLabel = generateAngleLabel();
  const opposite = angleAt === "bottom" ? vertical : horizontal;
  const adjacent = angleAt === "bottom" ? horizontal : vertical;
  const numerator = kind === "sin" ? opposite : kind === "cos" ? adjacent : opposite;
  const denominator = kind === "tan" ? adjacent : hypotenuse;

  return {
    type,
    data: {
      kind,
      angleLabel,
      angleAt,
      mirrored,
      labels: { horizontal, vertical, hypotenuse },
      numerator,
      denominator,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>
        Na podstawie rysunku zapisz <InlineMath math={functionLatex(data.kind, data.angleLabel)} /> jako iloraz
        odpowiednich boków oznaczonych literami <InlineMath math="x" />, <InlineMath math="y" /> i <InlineMath math="z" />.
      </p>
      <TrigRatioDiagram
        labels={data.labels}
        mirrored={data.mirrored}
        angleAt={data.angleAt}
        angleLabel={data.angleLabel}
      />
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label={functionLatex(data.kind, data.angleLabel)}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer?.raw : userAnswer;
  return normalize(raw) === `${data.numerator}/${data.denominator}`;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`${functionLatex(data.kind, data.angleLabel)}=\\dfrac{${data.numerator}}{${data.denominator}}`} />;
}

export function renderExplanation({ data }) {
  const role = data.kind === "sin"
    ? "przyprostokątną leżącą naprzeciw kąta i przeciwprostokątną"
    : data.kind === "cos"
    ? "przyprostokątną przyległą do kąta i przeciwprostokątną"
    : "przyprostokątną leżącą naprzeciw kąta i przyprostokątną przyległą";
  return (
    <div className="space-y-3">
      <p>Najpierw rozpoznajemy {role}. Położenie rysunku ani nazwy boków nie zmieniają definicji.</p>
      <TrigRatioDiagram
        labels={data.labels}
        mirrored={data.mirrored}
        angleAt={data.angleAt}
        angleLabel={data.angleLabel}
      />
      <BlockMath math={`${functionLatex(data.kind, data.angleLabel)}=\\dfrac{${data.numerator}}{${data.denominator}}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer, task) {
  const raw = typeof userAnswer === "object" ? userAnswer?.raw : userAnswer;
  if (!raw) return <i>(brak odpowiedzi)</i>;
  return (
    <InlineMath
      math={formatLatexPreview(
        raw,
        functionLatex(task?.data?.kind || "sin", task?.data?.angleLabel)
      )}
    />
  );
}
